import _ from 'lodash';

/**
 * Play and control vimeo videos
 * @see https://developer.vimeo.com/player/js-api
 */
export class VideoPlayerVimeo {
	/**
	 * @param {Element} element  The element to which to render the video iframe
	 */
	constructor(element) {
		/**
		 * The element to which to render the video iframe
		 * @type {Element}
		 */
		this.element = element;
		/**
		 * The type of video player ("vimeo")
		 * @type {String}
		 */
		this.type = 'vimeo';
		/**
		 * The number of milliseconds after which to consider the playback failed
		 * @type {Number}
		 */
		this.timeout = 5000;
		/**
		 * Volume where 0 is muted and 1 is full volume
		 * @type {number}
		 */
		this.volume = 1;
		window.addEventListener('message', this._onPostMessage.bind(this));
	}

	/**
	 * Determine if the given URL is a playable Vimeo video URL
	 * @param {String} url
	 * @returns {boolean}
	 */
	static isPlayable(url) {
		return !!this.extractVideoId(url);
	}

	/**
	 * Play the given url
	 * @param {String} url
	 * @returns {Promise}  Resolves on playback end and rejects on playback error
	 */
	play(url) {
		return new Promise((resolve, reject) => {
			/** @private */
			this._onVideoEnded = resolve;
			/** @private */
			this._onVideoError = reject;
			this._appendIframe(this.element, url);
		});
	}

	/**
	 * Extract the video ID from a URL. Note that we can full details from their open API using
	 * https://vimeo.com/api/oembed.json?url=$url
	 * for example https://vimeo.com/api/oembed.json?url=https://vimeo.com/121303971
	 * @param {String} url
	 * @returns {String|null}  The ID or null if the id was not found
	 */
	static extractVideoId(url) {
		if (typeof url != 'string') {
			return null;
		}
		let regexes = [
			// review video such as https://vimeo.com/sharpr/review/121303971/fbf1a3092f
			/^https?:\/\/[\w.-]*\bvimeo\.com\/.+\/review\/(\d+)/,
			// album video such as https://vimeo.com/album/2222222/video/11111111
			/^https?:\/\/[\w.-]*\bvimeo\.com\/album\/.+\/video\/(\d+)/,
			// regular video such as https://vimeo.com/11111111
			// groups video such as http://vimeo.com/groups/name/videos/11111111
			// channels video such as http://vimeo.com/channels/11111111
			/^https?:\/\/[\w.-]*\bvimeo\.com\/(?:.+\/)?(\d+)/,
			// domain can be vimeo.com, player.vimeo.com, www.vimeo.com
		];
		for (let i = 0; i < regexes.length; i++) {
			let match = url.match(regexes[i]);
			if (match) {
				return match[1];
			}
		}
		return null;
	}

	/**
	 * Send a command to the iframe using the postMessage() Vimeo API
	 * @param {String} method  The name of the API method
	 * @param {*} [value]  The argument
	 */
	api(method, value = undefined) {
		if (!this.iframeOrigin) {
			// not ready yet
			return;
		}
		let msg = { method, value };
		this.iframe.contentWindow.postMessage(msg, this.iframeOrigin);
	}

	/**
	 * Suspend playback
	 */
	pause() {
		this.api('pause');
	}

	/**
	 * Stop playback and remove the iframe element
	 */
	stop() {
		this._cleanup();
	}

	/**
	 * Set the volume where 0 is muted and 1 is full volume
	 * @param {Number} volume
	 */
	setVolume(volume) {
		if (typeof volume == 'number' && volume >= 0 && volume <= 1) {
			this.volume = volume;
		}
		this.api('setVolume', this.volume);
	}

	/**
	 * Append the iframe to the given element
	 * @param {Element} element
	 * @param {String} url
	 * @private
	 */
	_appendIframe(element, url) {
		this._cleanup();
		let vid = VideoPlayerVimeo.extractVideoId(url);
		/** @private */
		this.playerId = _.uniqueId('vimeo');
		/**
		 * The iframe element used
		 * @type {Element}
		 */
		this.iframe = document.createElement('iframe');
		this.iframe.setAttribute('width', '640');
		this.iframe.setAttribute('height', '390');
		this.iframe.setAttribute('id', this.playerId);
		this.iframe.setAttribute('frameborder', '0');
		this.iframe.onload = this._onFrameLoad.bind(this);
		this.iframe.onerror = this._onVideoError;
		this.iframe.src = `https://player.vimeo.com/video/${vid}?api=1&player_id=${this.playerId}`;
		element.appendChild(this.iframe);
	}

	/**
	 * Handler for messages from iframe from the Vimeo API
	 * @param {Object} evt
	 * @private
	 */
	_onPostMessage(evt) {
		if (!/^https?:\/\/player.vimeo.com/.test(evt.origin)) {
			// not a vimeo player message we need to handle
			return;
		}
		let data;
		try {
			data = JSON.parse(evt.data);
		} catch (e) {
			// unexpected message
			return;
		}
		if (data.player_id !== this.playerId) {
			// message from another iframe on the page
			return;
		}
		// console.log('got vimeo window event! evt=', evt);
		/**
		 * The origin of the iframe, typically "https://player.vimeo.com"
		 * @type {String}
		 */
		this.iframeOrigin = evt.origin;
		if (data.event == 'ready') {
			this.api('addEventListener', 'finish');
			this.api('play');
			if (typeof this.volume == 'number') {
				this.setVolume(this.volume);
			}
		} else if (data.event == 'finish') {
			this._onVideoEnded();
		}
	}

	/**
	 * Handle the frame load event
	 * @private
	 */
	_onFrameLoad() {
		setTimeout(() => {
			if (!this.iframeOrigin) {
				// we never got the ready signal;
				// embedding video is prevented
				// by the video author's privacy settings
				// or there was an embed error
				this._cleanup();
				this._onVideoEnded();
			}
		}, this.timeout);
	}

	/**
	 * Remove the iframe from its parent
	 * @private
	 */
	_cleanup() {
		if (this.iframe) {
			this.iframe.parentNode.removeChild(this.iframe);
		}
		this.iframe = null;
		this.iframeOrigin = null;
	}
}
